// 运行时配置

import { AxiosError, defineApp, history } from "@umijs/max";
import {
  getMemberBenefitService,
  getUserInfoService,
  IBenefit,
  IUserInfo,
} from "./services/user";
import { defaultAllowPaths, ResponseCode, routerPath } from "./constants";
import { message, notification, Row } from "antd";
import styles from "./index.less";
import UserHeader from "./components/UserHeader";
import Cookies from "js-cookie";
import { TOKEN } from "@/constants";
import { getSecondLevelDomain } from "@/utils";
import { backOffical, getBurialPointEnv } from "./utils/backOffical";
import ArmsRum from "@arms/rum-browser";
import { LayoutToken } from "../config/layoutToken";
import { useState } from "react";
import { MenuOutlined } from "@ant-design/icons";
import Logo from "@/assets/images/logo.png";
import LogoIcon from "@/assets/images/logo-icon.png";
import { initNotification } from "@/utils/notification";
import "@/utils/checkConnectionStatus";
import { damselfishPersistentStoreActions } from "./store";
import { wechatChatPersistentStoreActions } from "./pages/channel-chat/channel-chat-list/wechat-chat/store";
import { wecomChatPersistentStoreActions } from "./pages/channel-chat/channel-chat-list/wecom-chat/store";
import { wechatContactsPersistentStoreActions } from "./pages/channel-chat/channel-customer-list/wechat-contacts/store";
import { wecomContactsPersistentStoreActions } from "./pages/channel-chat/channel-customer-list/wecom-contacts/store";
import { mailChatPersistentStoreActions } from "./pages/channel-chat/channel-chat-list/email/store";
import { mailContactsPersistentStoreActions } from "./pages/channel-chat/channel-customer-list/mail-contacts/store";
import { whatsappChatPersistentStoreActions } from "./pages/channel-chat/channel-chat-list/whatsapp-chat/store";
import { whatsappContactsPersistentStoreActions } from "./pages/channel-chat/channel-customer-list/whatsapp-contacts/store";

import DownloadDesktop from "./components/DownloadDesktop";
import ExpireNoticeAndBannedMask from "./components/ExpireNoticeAndBannedMask";

import { ResourceItem, ResourceType } from "@packages/components";
import { PricingPlanVersionEnum } from "@packages/pricing-components";
import { getMemberPlanDetailService } from "@packages/pricing-components/src/service";
import dayjs from "dayjs";
import { getResourceInfo, IResourceInfo } from "./services/resource";

const isProd = getBurialPointEnv() === "prod";
if (isProd) {
  ArmsRum.init({
    pid: "hmsa4pcrpj@22d71f413474479",
    endpoint: "https://hmsa4pcrpj-default-cn.rum.aliyuncs.com",
    // 设置环境信息，参考值：'prod' | 'gray' | 'pre' | 'daily' | 'local'
    env: getBurialPointEnv(),
    // 设置路由模式， 参考值：'history' | 'hash'
    spaMode: "history",
    collectors: {
      // 页面性能指标监听开关，默认开启
      perf: true,
      // WebVitals指标监听开关，默认开启
      webVitals: true,
      // Ajax监听开关，默认开启
      api: true,
      // 静态资源开关，默认开启
      staticResource: true,
      // JS错误监听开关，默认开启
      jsError: true,
      // 控制台错误监听开关，默认开启
      consoleError: true,
      // 用户行为监听开关，默认开启
      action: true,
    },
    // 链路追踪配置开关，默认关闭
    tracing: true,
    filters: {
      exception: [
        "CanceledError",
        "canceled",
        "Request failed with status code 401",
      ],
    },
  });
}
/** 用户允许访问的路径 */
let allowPaths: string[] = defaultAllowPaths;

const updateAllowPaths = (paths: string[]) => {
  allowPaths = [...defaultAllowPaths, ...paths];
};

let firstMenuPath = "";

const channelPathnames = [
  routerPath.CHANNEL_CHAT_LIST_WECOM_CHAT,
  routerPath.CHANNEL_CHAT_LIST_WECHAT_CHAT,
  routerPath.CHANNEL_CHAT_LIST_MAIL_CHAT,
  routerPath.CHANNEL_CHAT_LIST_WHATSAPP_CHAT,
  routerPath.CHANNEL_CUSTOMER_LIST_WECOM_CONTACTS,
  routerPath.CHANNEL_CUSTOMER_LIST_WECHAT_CONTACTS,
  routerPath.CHANNEL_CUSTOMER_LIST_MAIL_CONTACTS,
  routerPath.CHANNEL_CUSTOMER_LIST_WHATSAPP_CONTACTS,
];

/**
 * 用户套餐订阅信息
 */
interface IUserPlanSubscriptionInformation {
  paidVersion: PricingPlanVersionEnum;
  trailPlan: boolean;
  expireAt: string;
  isExpired: boolean;
  showExpireNotice: boolean;
}

interface IInitialState {
  userInfo?: IUserInfo;
  menuList: ResourceItem[];
  allowPaths: string[];
  updateAllowPaths: (paths: string[]) => void;
  pagePerms: IUserInfo["pagePerms"];
  /** 用户权益 */
  userBenefits: IBenefit[];
  /** 用户订阅计划 */
  userPlanSubscriptionInformation?: IUserPlanSubscriptionInformation;
  /** 用户资源信息 */
  userResourceInformation?: IResourceInfo;
}

export async function getInitialState(): Promise<IInitialState> {
  const initialState: IInitialState = {
    userInfo: void 0,
    menuList: [],
    allowPaths: [],
    updateAllowPaths: () => {},
    pagePerms: [],
    userBenefits: [],
    userPlanSubscriptionInformation: void 0,
    userResourceInformation: void 0,
  };

  if (
    [
      routerPath.CHAT,
      routerPath.WHATSAPP_CONNECT,
      routerPath.WHATSAPP_CONNECTED,
    ].includes(window.location.pathname)
  ) {
    return initialState;
  }

  initNotification();

  const rsp = await getUserInfoService();

  if (rsp.code === ResponseCode.Success) {
    if (isProd) {
      const nickName = rsp?.data?.user?.nickName;
      const userId = rsp?.data?.user?.userId;
      try {
        ArmsRum.setConfig("user", {
          name: nickName,
          tags: userId,
        });
      } catch (error) {
        console.error("ArmsRum.setConfig error", error);
      }
    }
    // 递归收集所有路径
    const collectMenuPaths = (resourceList: ResourceItem[]) => {
      if (!resourceList || !resourceList.length) return;
      for (const resource of resourceList) {
        if (resource.path) {
          allowPaths.push(resource.path);
        }
        if (
          resource.type === ResourceType.MENU &&
          resource.children &&
          resource.children.length
        ) {
          collectMenuPaths(resource.children);
        }
      }
    };
    collectMenuPaths(rsp.data?.menus || []);
    firstMenuPath = rsp.data?.menus?.[0]?.path || "";

    const planRsp = await getMemberPlanDetailService();

    if (planRsp.code !== ResponseCode.Success) {
      message.error(planRsp.msg);
      return initialState;
    }

    /** 没买过，重定向到官网 */
    if (!planRsp?.data?.memberPlan) {
      backOffical();
      return initialState;
    }

    const resourceRsp = await getResourceInfo();

    if (resourceRsp.code !== ResponseCode.Success) {
      message.error(resourceRsp.msg);
      return initialState;
    }

    const benefitRsp = await getMemberBenefitService();
    if (benefitRsp.code !== ResponseCode.Success) {
      message.error(benefitRsp.msg);
      return initialState;
    }

    return {
      userInfo: rsp.data,
      menuList: rsp.data?.menus || [],
      allowPaths,
      updateAllowPaths,
      pagePerms: rsp.data?.pagePerms || [],
      userBenefits: benefitRsp.data || [],
      userPlanSubscriptionInformation: {
        paidVersion: planRsp?.data?.memberPlan,
        trailPlan: planRsp?.data?.trailPlan ?? false,
        expireAt: dayjs(planRsp?.data?.endTime)
          .subtract(1, "day")
          .format("YYYY-MM-DD"),
        isExpired: dayjs().isAfter(dayjs(planRsp?.data?.endTime)),
        showExpireNotice:
          dayjs(planRsp?.data?.endTime).diff(dayjs(), "day") <= 7,
      },
      userResourceInformation: resourceRsp?.data,
    };
  }

  if (window.location.pathname !== routerPath.UNREAD_CONVERSATION_LIST) {
    message.error(rsp.msg);
  }

  return initialState;
}

const codeMessage = {
  200: "服务器成功返回请求的数据。",
  201: "新建或修改数据成功。",
  202: "一个请求已经进入后台排队（异步任务）。",
  204: "删除数据成功。",
  400: "发出的请求有错误，服务器没有进行新建或修改数据的操作。",
  401: "用户没有权限（令牌、用户名、密码错误）。",
  403: "用户得到授权，但是访问是被禁止的。",
  404: "发出的请求针对的是不存在的记录，服务器没有进行操作。",
  406: "请求的格式不可得。",
  410: "请求的资源被永久删除，且不会再得到的。",
  419: "接口权限被禁止",
  422: "当创建一个对象时，发生一个验证错误。",
  500: "服务器发生错误，请检查服务器。",
  502: "网关错误。",
  503: "服务不可用，服务器暂时过载或维护。",
  504: "网关超时。",
};

export default defineApp({
  request: {
    responseInterceptors: [
      [
        (rsp) => {
          return rsp;
        },
        (error) => {
          if (
            window.location.pathname === routerPath.UNREAD_CONVERSATION_LIST
          ) {
            return Promise.reject(error);
          }

          const axiosError = error as unknown as AxiosError;
          // 这部分都是报错提示
          if (axiosError?.code !== "ERR_CANCELED") {
            if (axiosError.code !== "ERR_NETWORK") {
              if (axiosError.response?.status === 200) {
                notification.error({
                  message: "接口报错",
                  description: axiosError?.response?.config?.url,
                });
              } else {
                notification.error({
                  message:
                    (axiosError?.response?.data as any)?.msg ||
                    codeMessage[
                      axiosError?.response?.status as keyof typeof codeMessage
                    ],
                  description: axiosError?.response?.config?.url,
                });
              }
            } else {
              notification.error({
                message: "网络错误",
                description: "网络已断开连接",
              });
            }
          }

          // 这部分是登录过期处理
          if ((error as unknown as AxiosError)?.response?.status === 401) {
            damselfishPersistentStoreActions.reset();
            wechatChatPersistentStoreActions.reset();
            wecomChatPersistentStoreActions.reset();
            mailChatPersistentStoreActions.reset();
            whatsappChatPersistentStoreActions.reset();
            wechatContactsPersistentStoreActions.reset();
            wecomContactsPersistentStoreActions.reset();
            mailContactsPersistentStoreActions.reset();
            whatsappContactsPersistentStoreActions.reset();
            if (process.env.NODE_ENV !== "development") {
              Cookies.remove(TOKEN);
              Cookies.remove(TOKEN, { domain: getSecondLevelDomain() });
              setTimeout(() => {
                if (location.host.startsWith("dev")) {
                  window.location.href = `https://devweb.12times.com`;
                } else if (location.host.startsWith("qas")) {
                  window.location.href = `https://qasweb.12times.com`;
                } else {
                  window.location.href = `https://web.12times.com`;
                }
              }, 1000);
            }
          }
          throw error;
        },
      ],
    ],
  },
  layout(initData) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [collapsed, setCollapsed] = useState(false);

    return {
      className: styles.layout,
      rightContentRender: false,
      menuHeaderRender: (logo, title) => {
        return (
          <UserHeader
            showHeader={!!title}
            collapsed={collapsed}
            onCollapsed={setCollapsed}
          />
        );
      },
      headerRender() {
        return (
          <Row justify="center" align="middle" className="h-full">
            <MenuOutlined
              onClick={() => setCollapsed(false)}
              className="text-2xl absolute top-0 left-4 bottom-0 m-auto"
            />
            <img src={Logo} alt="12Times" />
          </Row>
        );
      },
      menuFooterRender() {
        return collapsed ? (
          <img src={LogoIcon} className="h-8 w-8 mx-auto mb-9" />
        ) : (
          <DownloadDesktop />
        );
      },
      logo: false,
      collapsedButtonRender: false,
      collapsed,
      onCollapse: setCollapsed,
      onMenuHeaderClick() {},
      menuDataRender() {
        const menuList = initData?.initialState?.menuList || [];
        const menuCalc = (menuData: ResourceItem[]) => {
          if (!menuData || menuData.length === 0) return [];
          return menuData.map((item) => {
            return {
              ...item,
              icon: <img src={item.icon} />,
            };
          });
        };
        return menuCalc(menuList);
      },
      token: LayoutToken,
      menuProps: { rootClassName: styles.menu },
      siderWidth: 256,
      childrenRender(dom) {
        const showExpireNotice =
          initData?.initialState?.userPlanSubscriptionInformation
            ?.showExpireNotice;
        const isPricingPage = window.location.pathname === routerPath.PRICING;

        // 资源管理页面不显示 mask
        const isExcludeBannedMask = [
          routerPath.RESOURCE_MY,
          routerPath.RESOURCE_ORDER,
        ].includes(window.location.pathname);

        return (
          <div className="relative h-screen overflow-y-auto">
            {!isPricingPage && showExpireNotice && (
              <ExpireNoticeAndBannedMask
                isExcludeBannedMask={isExcludeBannedMask}
              />
            )}
            {dom}
          </div>
        );
      },
    };
  },
  onRouteChange(props) {
    const pathname = props.location.pathname;
    const routes = props.routes || [];
    const route = Object.values(routes)?.find((route: any) => {
      // 精确匹配路由
      if (route.path === pathname) {
        return true;
      }
      // 处理通配符路由，如 "/ccgpt/*"
      if (route.path && route.path.includes("*")) {
        const pathPrefix = route.path.split("*")[0];
        if (pathname.startsWith(pathPrefix)) {
          return true;
        }
      }
      // 处理重定向路由
      if (route.redirect && pathname === route.path) {
        return true;
      }
      return false;
    });
    if (pathname === "/") {
      history.replace(firstMenuPath);
    } else if (!route && pathname !== routerPath.ERROR_404) {
      history.replace(routerPath.ERROR_404);
    } else if (
      !allowPaths.includes(pathname) &&
      pathname !== routerPath.ERROR_403
    ) {
      // 渠道相关的页面在403的时候需要做特殊处理，需要设置channelType为undefined
      if (channelPathnames.includes(pathname)) {
        damselfishPersistentStoreActions.setChannelType(undefined);
      }
      message.error("您没有权限访问该页面");
      history.replace(routerPath.ERROR_403);
    }
  },
});
